import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
    ArrowLeft,
    Users,
    Store,
    BarChart3,
    Cpu,
    ShieldCheck,
    Trash2,
    ExternalLink,
    Sparkles,
} from "lucide-react";
import { api, absUrl, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { formatPrice, timeAgo } from "@/lib/constants";
import VinnyBrain from "@/pages/VinnyBrain";

/**
 * Unified S.C.A.R.S. Control Panel.
 *   /admin/control          → default section
 *   /admin/control/:section → accounts | business | operations | brain
 *
 * Sidebar-driven single-pane admin cockpit. Absorbs old /admin and links
 * out to /admin/vinny (via the "B.R.A.I.N." section which mounts it inline).
 */

const SECTIONS = [
    {
        key: "accounts",
        label: "Accounts",
        icon: Users,
        subtitle: "Users · Verification · Roles",
    },
    {
        key: "business",
        label: "Business",
        icon: Store,
        subtitle: "Listings · Posts · Moderation",
    },
    {
        key: "operations",
        label: "Operations",
        icon: BarChart3,
        subtitle: "Analytics · Health · Metrics",
    },
    {
        key: "brain",
        label: "B.R.A.I.N.",
        icon: Cpu,
        subtitle: "Content · SEO · AI Visibility",
    },
    {
        key: "playgarage",
        label: "Play Garage",
        icon: Sparkles,
        subtitle: "Frozen v1 sandbox",
        href: "/admin/play-garage",
    },
];

export default function ControlPanel() {
    const { section } = useParams();
    const navigate = useNavigate();
    const active = SECTIONS.find((s) => s.key === section) ? section : "accounts";

    return (
        <div className="min-h-screen bg-onyx-900 text-white">
            <Helmet>
                <title>Control Panel · SRT Finder</title>
            </Helmet>

            {/* Header — sharp, angular */}
            <header
                className="sticky top-0 z-30"
                style={{
                    background: "rgba(6,6,6,0.92)",
                    borderBottom: "1px solid #262626",
                    backdropFilter: "blur(18px)",
                    WebkitBackdropFilter: "blur(18px)",
                }}
            >
                <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
                    <button
                        onClick={() => navigate(-1)}
                        className="btn-ghost px-2.5 py-2"
                        style={{ borderRadius: 4 }}
                        data-testid="control-back-btn"
                    >
                        <ArrowLeft className="w-4 h-4" />
                    </button>
                    <div className="flex items-center gap-2">
                        <ShieldCheck className="w-5 h-5 text-hellcat" />
                        <div>
                            <div
                                className="headline text-base leading-none"
                                style={{ letterSpacing: "0.04em" }}
                            >
                                S.C.A.R.S. Control Panel
                            </div>
                            <div
                                className="text-[10px] text-onyx-500 mt-1"
                                style={{ letterSpacing: "0.22em" }}
                            >
                                SUPERCHARGED CONTENT &amp; AUTHORITY RANKING SYSTEM
                            </div>
                        </div>
                    </div>
                    <div className="ml-auto flex items-center gap-2">
                        <Link
                            to="/admin/vinny"
                            className="hidden md:inline-flex items-center gap-1.5 px-3 py-2 text-[11px] font-bold text-onyx-300 hover:text-white"
                            style={{
                                background: "#141414",
                                border: "1px solid #262626",
                                borderRadius: 4,
                                letterSpacing: "0.08em",
                            }}
                            data-testid="open-brain-standalone"
                        >
                            Open B.R.A.I.N. Fullscreen
                            <ExternalLink className="w-3.5 h-3.5" />
                        </Link>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-4 py-5 flex flex-col md:flex-row gap-5">
                {/* Sidebar — sharp cards, no pills */}
                <aside
                    className="md:w-64 shrink-0"
                    data-testid="control-sidebar"
                    aria-label="Control panel sections"
                >
                    <nav className="flex md:flex-col gap-2 overflow-x-auto no-scrollbar">
                        {SECTIONS.map((s) => {
                            const isActive = !s.href && s.key === active;
                            const handleClick = () => {
                                if (s.href) navigate(s.href);
                                else navigate(`/admin/control/${s.key}`);
                            };
                            return (
                                <button
                                    key={s.key}
                                    onClick={handleClick}
                                    data-testid={`section-${s.key}`}
                                    className="text-left px-4 py-3 shrink-0 md:shrink flex items-center gap-3"
                                    style={{
                                        borderRadius: 6,
                                        background: isActive
                                            ? "rgba(225,6,0,0.14)"
                                            : "#141414",
                                        border: isActive
                                            ? "1px solid #E10600"
                                            : "1px solid #262626",
                                        color: isActive ? "#fff" : "rgba(255,255,255,0.75)",
                                        boxShadow: isActive
                                            ? "0 8px 22px rgba(225,6,0,0.22)"
                                            : "none",
                                        transition:
                                            "background-color 0.15s, border-color 0.15s",
                                        minWidth: 190,
                                    }}
                                >
                                    <div
                                        className="w-9 h-9 flex items-center justify-center shrink-0"
                                        style={{
                                            borderRadius: 4,
                                            background: isActive
                                                ? "rgba(225,6,0,0.22)"
                                                : "rgba(255,255,255,0.05)",
                                            border: "1px solid rgba(255,255,255,0.06)",
                                        }}
                                    >
                                        <s.icon
                                            className={`w-4 h-4 ${
                                                isActive ? "text-hellcat" : "text-onyx-300"
                                            }`}
                                            strokeWidth={2.2}
                                        />
                                    </div>
                                    <div className="min-w-0">
                                        <div
                                            className="text-sm font-bold leading-tight"
                                            style={{ letterSpacing: "0.04em" }}
                                        >
                                            {s.label}
                                        </div>
                                        <div className="text-[10px] text-onyx-500 mt-0.5 truncate">
                                            {s.subtitle}
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </nav>
                </aside>

                {/* Content */}
                <main className="flex-1 min-w-0">
                    {active === "accounts" && <AccountsSection />}
                    {active === "business" && <BusinessSection />}
                    {active === "operations" && <OperationsSection />}
                    {active === "brain" && <BrainSection />}
                </main>
            </div>
        </div>
    );
}

/* -------------------------------- Accounts -------------------------------- */

function AccountsSection() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/admin/users")
            .then(({ data }) => setUsers(data))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    }, []);

    async function toggleVerify(u) {
        try {
            const { data } = await api.patch(`/admin/users/${u.user_id}/verify`, {
                verified: !u.verified_seller,
            });
            setUsers((cur) => cur.map((x) => (x.user_id === data.user_id ? data : x)));
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    return (
        <SectionShell
            title="Accounts"
            eyebrow="USERS · VERIFICATION"
            description="Manage member accounts, verified seller badges, and admin roles."
        >
            <div
                className="overflow-hidden"
                style={{
                    borderRadius: 6,
                    background: "#0d0d0d",
                    border: "1px solid #262626",
                }}
                data-testid="accounts-table"
            >
                <table className="w-full text-sm">
                    <thead className="bg-onyx-800 text-onyx-500 label-caps text-[10px]">
                        <tr>
                            <th className="text-left p-3">User</th>
                            <th className="text-left p-3 hidden sm:table-cell">Email</th>
                            <th className="text-left p-3">Role</th>
                            <th className="text-left p-3">Verified</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td colSpan={4} className="p-6 text-center text-onyx-500">
                                    Loading users…
                                </td>
                            </tr>
                        )}
                        {!loading && users.length === 0 && (
                            <tr>
                                <td colSpan={4} className="p-6 text-center text-onyx-500">
                                    No users yet.
                                </td>
                            </tr>
                        )}
                        {users.map((u) => (
                            <tr key={u.user_id} className="border-t border-onyx-600">
                                <td className="p-3 flex items-center gap-2">
                                    {u.avatar_url ? (
                                        <img
                                            src={absUrl(u.avatar_url)}
                                            className="w-7 h-7 object-cover"
                                            style={{ borderRadius: 4 }}
                                            alt=""
                                        />
                                    ) : (
                                        <div
                                            className="w-7 h-7 surface-elevated"
                                            style={{ borderRadius: 4 }}
                                        />
                                    )}
                                    <span className="truncate">{u.name}</span>
                                </td>
                                <td className="p-3 text-onyx-500 hidden sm:table-cell truncate max-w-[220px]">
                                    {u.email}
                                </td>
                                <td className="p-3">
                                    {u.role === "admin" ? (
                                        <span className="label-caps text-hellcat">
                                            Admin
                                        </span>
                                    ) : (
                                        <span className="text-onyx-500">User</span>
                                    )}
                                </td>
                                <td className="p-3">
                                    <button
                                        onClick={() => toggleVerify(u)}
                                        data-testid={`verify-toggle-${u.user_id}`}
                                        className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest ${
                                            u.verified_seller
                                                ? "bg-hellcat text-white"
                                                : "btn-ghost"
                                        }`}
                                        style={{ borderRadius: 4 }}
                                    >
                                        {u.verified_seller ? "Verified" : "Grant"}
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </SectionShell>
    );
}

/* -------------------------------- Business -------------------------------- */

function BusinessSection() {
    const [listings, setListings] = useState([]);
    const [posts, setPosts] = useState([]);
    const [tab, setTab] = useState("listings");

    useEffect(() => {
        (async () => {
            try {
                const [l, p] = await Promise.all([
                    api.get("/admin/listings"),
                    api.get("/admin/posts"),
                ]);
                setListings(l.data);
                setPosts(p.data);
            } catch (e) {
                toast.error(formatApiError(e));
            }
        })();
    }, []);

    async function setListingStatus(l, status) {
        try {
            const { data } = await api.patch(`/admin/listings/${l.listing_id}/status`, {
                status,
            });
            setListings((cur) =>
                cur.map((x) => (x.listing_id === l.listing_id ? data : x))
            );
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    async function delPost(p) {
        if (!window.confirm("Delete this post permanently?")) return;
        await api.delete(`/admin/posts/${p.post_id}`);
        setPosts((cur) => cur.filter((x) => x.post_id !== p.post_id));
    }

    return (
        <SectionShell
            title="Business"
            eyebrow="LISTINGS · POSTS · MODERATION"
            description="Hide/activate listings and moderate community posts."
        >
            {/* Sharp tab switcher */}
            <div className="inline-flex p-1 gap-1 mb-4"
                style={{
                    background: "#141414",
                    border: "1px solid #262626",
                    borderRadius: 6,
                }}
            >
                {[
                    { key: "listings", label: "Listings" },
                    { key: "posts", label: "Posts" },
                ].map((t) => {
                    const isActive = tab === t.key;
                    return (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            data-testid={`business-tab-${t.key}`}
                            className="px-4 py-1.5 text-sm font-bold"
                            style={{
                                borderRadius: 4,
                                background: isActive ? "#E10600" : "transparent",
                                color: isActive ? "#fff" : "rgba(255,255,255,0.7)",
                                letterSpacing: "0.04em",
                            }}
                        >
                            {t.label}
                        </button>
                    );
                })}
            </div>

            {tab === "listings" && (
                <div className="space-y-2">
                    {listings.length === 0 && (
                        <EmptyRow label="No listings yet." />
                    )}
                    {listings.map((l) => (
                        <div
                            key={l.listing_id}
                            className="surface p-3 flex items-center gap-3"
                            style={{ borderRadius: 6 }}
                        >
                            {l.photos?.[0] && (
                                <img
                                    src={absUrl(l.photos[0])}
                                    className="w-16 h-12 object-cover"
                                    style={{ borderRadius: 4 }}
                                    alt=""
                                />
                            )}
                            <div className="flex-1 min-w-0">
                                <div className="headline text-sm truncate">
                                    {l.year} {l.model_name} {l.trim}
                                </div>
                                <div className="text-xs text-onyx-500 truncate">
                                    {formatPrice(l.price)} · {l.status} ·{" "}
                                    {timeAgo(l.created_at)}
                                </div>
                            </div>
                            <div className="flex gap-2 shrink-0">
                                <button
                                    onClick={() => setListingStatus(l, "hidden")}
                                    className="btn-ghost px-2 py-1 text-xs"
                                    style={{ borderRadius: 4 }}
                                    data-testid={`hide-listing-${l.listing_id}`}
                                >
                                    Hide
                                </button>
                                <button
                                    onClick={() => setListingStatus(l, "active")}
                                    className="btn-ghost px-2 py-1 text-xs"
                                    style={{ borderRadius: 4 }}
                                >
                                    Activate
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {tab === "posts" && (
                <div className="space-y-2">
                    {posts.length === 0 && <EmptyRow label="No posts yet." />}
                    {posts.map((p) => (
                        <div
                            key={p.post_id}
                            className="surface p-3 flex items-start gap-3"
                            style={{ borderRadius: 6 }}
                        >
                            <div className="flex-1 min-w-0">
                                <div className="headline text-sm">{p.title}</div>
                                <div className="text-xs text-onyx-500 mt-1 line-clamp-2">
                                    {p.body}
                                </div>
                                <div className="text-[10px] text-onyx-500 mt-1 label-caps">
                                    {p.category} · {p.upvotes} upvotes ·{" "}
                                    {p.comment_count} comments
                                </div>
                            </div>
                            <button
                                onClick={() => delPost(p)}
                                data-testid={`delete-post-${p.post_id}`}
                                className="btn-ghost px-2.5 py-2 text-hellcat"
                                style={{ borderRadius: 4 }}
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </SectionShell>
    );
}

/* ------------------------------- Operations ------------------------------- */

function OperationsSection() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/admin/analytics")
            .then(({ data }) => setStats(data))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    }, []);

    return (
        <SectionShell
            title="Operations"
            eyebrow="ANALYTICS · METRICS"
            description="Platform health snapshot."
        >
            {loading && (
                <div className="text-sm text-onyx-500 py-6">Loading metrics…</div>
            )}
            {!loading && stats && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {Object.entries(stats).map(([k, v]) => (
                        <div
                            key={k}
                            className="surface p-4"
                            style={{ borderRadius: 6 }}
                            data-testid={`metric-${k}`}
                        >
                            <div className="label-caps">{k.replace(/_/g, " ")}</div>
                            <div className="headline text-3xl mt-1 text-white">{v}</div>
                        </div>
                    ))}
                </div>
            )}
        </SectionShell>
    );
}

/* --------------------------------- B.R.A.I.N. ----------------------------- */

function BrainSection() {
    // Reuse the existing VinnyBrain page inline. It's fully self-contained.
    return (
        <div
            className="overflow-hidden"
            style={{
                borderRadius: 6,
                border: "1px solid #262626",
                background: "#0a0a0a",
            }}
            data-testid="brain-embed"
        >
            <VinnyBrain />
        </div>
    );
}

/* --------------------------------- helpers -------------------------------- */

function SectionShell({ title, eyebrow, description, children }) {
    return (
        <section>
            <div className="mb-4">
                <div
                    className="text-[10px] text-onyx-500 mb-1"
                    style={{ letterSpacing: "0.24em" }}
                >
                    {eyebrow}
                </div>
                <h1
                    className="text-white text-2xl sm:text-3xl leading-tight"
                    style={{
                        fontFamily: "'Alfa Slab One', serif",
                        fontWeight: 400,
                    }}
                >
                    {title}
                </h1>
                {description && (
                    <p className="text-sm text-onyx-500 mt-1">{description}</p>
                )}
            </div>
            {children}
        </section>
    );
}

function EmptyRow({ label }) {
    return (
        <div
            className="text-sm text-onyx-500 p-4 text-center"
            style={{
                borderRadius: 6,
                background: "#141414",
                border: "1px dashed #262626",
            }}
        >
            {label}
        </div>
    );
}
